/**
 * Single definition of "shows on Triage Queue" / sidebar badge (useAttentionCounts),
 * and of "shows on Needs Definition" (itemNeedsDefinition). TriageQueue, the
 * ItemListView view modes, the sidebar badges and the Pipeline Health report all
 * count with these predicates, so one number is one number everywhere.
 */

import type { TriageAssessment } from '../types/prioritization/triage';
import type { Candidate } from '../types/prioritization/lifecycle';
import { everyItemDefinitionGaps } from './itemReadiness';
import { useDefinitionPolicyStore } from '../stores/prioritization/definitionPolicyStore';

export function itemLifecycleStage(item: { lifecycle_stage?: string; lifecycleStage?: string }): string | undefined {
  return item.lifecycleStage ?? item.lifecycle_stage;
}

export function itemExitStatus(item: { exit_status?: string; exitStatus?: string }): 'active' | 'deferred' | 'archived' {
  const es = item.exit_status ?? item.exitStatus;
  if (es === 'deferred' || es === 'archived' || es === 'active') return es;
  return 'active';
}

/** Item ids that have left the visible triage queue (same filter as TriageQueue.tsx). */
export function triageFinalizedOutOfQueueIds(assessments: Record<string, TriageAssessment>): Set<string> {
  return new Set(
    Object.entries(assessments)
      .filter(([, a]) => a.outcome !== 'needs_clarification')
      .map(([itemId]) => itemId)
  );
}

export function itemIsAwaitingTriage(
  item: { id: string; lifecycle_stage?: string; lifecycleStage?: string; exit_status?: string; exitStatus?: string },
  triagedOutIds: ReadonlySet<string>
): boolean {
  if (itemLifecycleStage(item) !== 'intake') return false;
  if (itemExitStatus(item) !== 'active') return false;
  if (triagedOutIds.has(item.id)) return false;
  return true;
}

/**
 * Single definition of "shows on Needs Definition" / the sidebar badge.
 * The item misses one of the workspace's every-item rules about its own
 * description (Settings → Readiness → Every item, at Warning or Hard Stop —
 * values are assessment work and are not counted here), or it has an altitude
 * that the rules ask for but no altitude scoping answers. Archived items and
 * items still awaiting triage are not counted — triage comes first.
 */
export function itemNeedsDefinition(item: {
  id?: string;
  lifecycle_stage?: string;
  lifecycleStage?: string;
  exit_status?: string;
  exitStatus?: string;
  itemCategory?: string | null;
  altitudeLevel?: number | null;
  altitudeScopingInputs?: unknown;
  altitude_scoping_inputs?: unknown;
}): boolean {
  if (itemExitStatus(item) === 'archived') return false;
  if (itemLifecycleStage(item) === 'intake') return false;
  if (everyItemDefinitionGaps(item as unknown as Candidate).length > 0) return true;
  if (!item.altitudeLevel) return false;
  if (useDefinitionPolicyStore.getState().getBehavior('altitudeLevel') === 'optional') return false;
  const scopingInputs = item.altitudeScopingInputs ?? item.altitude_scoping_inputs;
  const hasScoping =
    scopingInputs != null &&
    typeof scopingInputs === 'object' &&
    Object.keys(scopingInputs as Record<string, unknown>).length > 0;
  return !hasScoping;
}
